import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, BarChart3, Download, Calendar, Filter,
  Users, Loader2, UserPlus, BookOpen,
  CheckCircle, Award, Trophy, Bell, RefreshCw, UserCheck,
  PlayCircle, PauseCircle, FileText, Zap, Star, Heart,
  Gift, Cpu, Database, Globe, Mail, MessageCircle, Building,
  Target, TrendingUp, AlertCircle, Info, Shield,
  Search, LogIn, LogOut, Edit
} from 'lucide-react';
import {
  dashboardService,
  TimeGranularity,
  ActivityStatus,
} from '@/services/dashboard.service';
import toast from 'react-hot-toast';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect from '@/components/ui/SimpleSelect';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { exportService } from '@/services/export.service';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { ACTIVITY_REPORT_PERMISSION } from '@/constants/constant-permissions';

// Metrics Display Configuration
const METRICS_DISPLAY_CONFIG = {
  eventOrder: [
    'USER_REGISTERED',
    'USER_LOGIN_SUCCESS',
    'PROFILE_UPDATED',
    'DEPARTMENT_CREATED',
    'ROLE_CREATED',
    'COURSE_STARTED',
    'COURSE_COMPLETED',
    'USER_LOGOUT'
  ],
  eventIconMap: {
    'USER_REGISTERED': 'user-plus',
    'USER_LOGIN_SUCCESS': 'log-in',
    'PROFILE_UPDATED': 'edit',
    'DEPARTMENT_CREATED': 'building',
    'ROLE_CREATED': 'shield',
    'COURSE_STARTED': 'play-circle',
    'COURSE_COMPLETED': 'check-circle',
    'USER_LOGOUT': 'log-out'
  }
};

export default function ActivityReport() {
  usePermissionRouteAccess([ACTIVITY_REPORT_PERMISSION.ADMIN_ACTIVITY_REPORT_READ, ACTIVITY_REPORT_PERMISSION.ADMIN_ACTIVITY_REPORT_WRITE]);
  const canWrite = useHasPermission([ACTIVITY_REPORT_PERMISSION.ADMIN_ACTIVITY_REPORT_WRITE]);

  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year' | 'all' | 'custom'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, DEFAULT_DEBOUNCE_DELAY);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<'csv' | 'excel' | null>(null);

  // Get granularity for API calls - returns undefined for 'all' time range
  const getGranularity = (range: string): TimeGranularity | undefined => {
    switch(range) {
      case 'week': return TimeGranularity.WEEKLY;
      case 'quarter': return TimeGranularity.QUARTERLY;
      case 'year': return TimeGranularity.YEARLY;
      case 'all': return undefined; // Don't send granularity for all time
      default: return TimeGranularity.MONTHLY;
    }
  };

  // Process and order the metrics
  const getOrderedMetrics = (eventsData: any[]) => {
    const eventMap: Record<string, any> = {};
    eventsData.forEach(event => {
      eventMap[event.event_type] = event;
    });

    const orderedEvents = METRICS_DISPLAY_CONFIG.eventOrder
      .map(eventType => {
        if (eventMap[eventType]) {
          return {
            ...eventMap[eventType],
            icon: (METRICS_DISPLAY_CONFIG.eventIconMap as Record<string, string>)[eventType] || eventMap[eventType].icon
          };
        }
        return null;
      })
      .filter(event => event !== null);

    return orderedEvents;
  };

  // Fetch activity metrics
  const { data: activityMetrics, isLoading: metricsLoading, error: metricsError } = useQuery({
    queryKey: ['activity-metrics', timeRange, startDate, endDate],
    queryFn: () => {
      if (timeRange === 'custom' && startDate && endDate) {
        return dashboardService.getActivityMetrics({
          start_date: startDate,
          end_date: endDate
        });
      }
      // For 'all' time range, don't send granularity
      if (timeRange === 'all') {
        return dashboardService.getActivityMetrics({});
      }
      return dashboardService.getActivityMetrics({
        granularity: getGranularity(timeRange)
      });
    },
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });

  // Fetch recent activities with pagination
  const { data: recentActivitiesResponse, isLoading: activitiesLoading, error: activitiesError } = useQuery({
    queryKey: ['recent-activities', currentPage, pageSize, debouncedSearchQuery, selectedStatus, timeRange, startDate, endDate],
    queryFn: () => {
      const params: any = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(debouncedSearchQuery.length > 0 && { action: debouncedSearchQuery }),
        ...(selectedStatus !== 'all' && { status: selectedStatus as ActivityStatus })
      };

      // Add granularity or custom dates
      if (timeRange === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (timeRange !== 'all') {
        // Only add granularity if not 'all' time range
        params.granularity = getGranularity(timeRange);
      }

      return dashboardService.getRecentActivitiesfilter(params);
    },
    refetchInterval: 60 * 1000,
    retry: 2,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    enabled: timeRange !== 'custom' || (timeRange === 'custom' && !!startDate && !!endDate),
    placeholderData: (previousData) => previousData
  });

  // Theme brand colour (CSS variable) with alpha, for inline styles
  const brandColor = (alpha: number) => `rgb(var(--brand-violet-600) / ${alpha})`;

  // Icon mapping function
  const getActivityIcon = (iconName: string) => {
    const iconProps = "w-4 h-4";
    
    switch (iconName.toLowerCase()) {
      case 'user-plus': return <UserPlus className={iconProps} />;
      case 'log-in': return <LogIn className={iconProps} />;
      case 'edit': return <Edit className={iconProps} />;
      case 'building': return <Building className={iconProps} />;
      case 'shield': return <Shield className={iconProps} />;
      case 'play-circle': return <PlayCircle className={iconProps} />;
      case 'check-circle': return <CheckCircle className={iconProps} />;
      case 'log-out': return <LogOut className={iconProps} />;
      case 'book-open': return <BookOpen className={iconProps} />;
      case 'award': return <Award className={iconProps} />;
      case 'trophy': return <Trophy className={iconProps} />;
      case 'bell': return <Bell className={iconProps} />;
      case 'refresh-cw': return <RefreshCw className={iconProps} />;
      case 'user-check': return <UserCheck className={iconProps} />;
      case 'pause-circle': return <PauseCircle className={iconProps} />;
      case 'file-text': return <FileText className={iconProps} />;
      case 'zap': return <Zap className={iconProps} />;
      case 'star': return <Star className={iconProps} />;
      case 'heart': return <Heart className={iconProps} />;
      case 'gift': return <Gift className={iconProps} />;
      case 'cpu': return <Cpu className={iconProps} />;
      case 'database': return <Database className={iconProps} />;
      case 'globe': return <Globe className={iconProps} />;
      case 'mail': return <Mail className={iconProps} />;
      case 'message-circle': return <MessageCircle className={iconProps} />;
      case 'users': return <Users className={iconProps} />;
      case 'target': return <Target className={iconProps} />;
      case 'trending-up': return <TrendingUp className={iconProps} />;
      case 'alert-circle': return <AlertCircle className={iconProps} />;
      default: return <Activity className={iconProps} />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success': return <CheckCircle className="w-4 h-4 text-brand-green-600" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-brand-yellow-600" />;
      case 'info': return <Info className="w-4 h-4 text-brand-blue-600" />;
      case 'error': return <AlertCircle className="w-4 h-4 text-red-600" />;
      default: return <Info className="w-4 h-4 text-gray-600" />;
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const minutes = Math.floor(diff / (1000 * 60));
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  };

  // Handle pagination
  const totalPages = Math.ceil((recentActivitiesResponse?.meta_data?.total || 0) / pageSize);
  const activities = recentActivitiesResponse?.data || [];

  // Reset to first page when debounced search changes
  useEffect(() => {
    if (debouncedSearchQuery !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery]);

  // Handle error notifications
  useEffect(() => {
    if (metricsError) {
      toast.error('Failed to load activity metrics');
    }
    if (activitiesError) {
      toast.error('Failed to load recent activities');
    }
  }, [metricsError, activitiesError]);

  // Handle export functionality - call export API
  const exportData = async (format: 'csv' | 'excel') => {
    try {
      setExportingFormat(format);

      // Use export service to call the export API with filters only
      const result = await exportService.exportActivityReport({
        searchQuery: debouncedSearchQuery,
        selectedStatus,
        timeRange,
        startDate,
        endDate,
        granularity: getGranularity(timeRange)
      }, format);

      toast.success(`Successfully exported ${result.count} activities as ${format.toUpperCase()}`);
    } catch (error: any) {
      console.error('Export error details:', error);
      let errorMessage = 'Failed to export activity report';

      if (error?.response?.status === 404) {
        errorMessage = 'Export feature is not available at this time';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Server error occurred during export. Please try again later.';
      } else if (error?.message) {
        errorMessage = `Export failed: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-violet-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Activity Reports</h1>
            <p className="text-brand-violet-100">
              Comprehensive activity monitoring and system usage analytics
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters
                  ? 'bg-white text-brand-violet-600'
                  : 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              <Filter className="w-5 h-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
            <button
              onClick={() => canWrite && exportData('excel')}
              disabled={!canWrite || exportingFormat !== null}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-brand-violet-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${canWrite ? 'hover:bg-brand-violet-50' : ''}`}
              title={!canWrite ? 'You do not have permission to export' : 'Export as Excel'}
            >
              {exportingFormat === 'excel' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">Excel</span>
            </button>
            <button
              onClick={() => canWrite && exportData('csv')}
              disabled={!canWrite || exportingFormat !== null}
              className={`flex items-center gap-2 px-4 py-2 bg-white text-brand-violet-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${canWrite ? 'hover:bg-brand-violet-50' : ''}`}
              title={!canWrite ? 'You do not have permission to export' : 'Export as CSV'}
            >
              {exportingFormat === 'csv' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">CSV</span>
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="mt-6 space-y-3">
          {/* Time Range Buttons Row */}
          <div className="flex flex-wrap gap-2">
            {['all', 'week', 'month', 'quarter', 'year', 'custom'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range as any)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold capitalize transition-all duration-300 transform hover:scale-105 ${
                  timeRange === range
                    ? 'bg-white text-brand-violet-600 shadow-xl scale-105'
                    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
              >
                {range === 'custom' && <Calendar className="w-4 h-4 inline mr-1" />}
                {range === 'all' ? 'All Time' : range}
              </button>
            ))}
          </div>

          {/* Custom Date Range - Inline Expansion */}
          {timeRange === 'custom' && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-white">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || undefined}
                  className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border-0 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-white">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  className="px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border-0 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
                />
              </div>
              {startDate && endDate && (
                <span className="text-sm font-medium text-white drop-shadow px-3 py-2 bg-white/20 rounded-lg">
                  {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))} days
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedStatus('all');
              }}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear All
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Activities</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search in activities..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-violet-500 focus:border-brand-violet-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <SimpleSelect
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(String(value || 'all'))}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'success', label: 'Success' },
                  { value: 'info', label: 'Info' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'error', label: 'Error' },
                ]}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Activity Metrics Overview */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Activity Metrics Overview</h2>
            <p className="text-sm text-gray-500 mt-1">
              {activityMetrics?.data?.total_events || 0} total events
              {timeRange === 'custom' && startDate && endDate
                ? ` from ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
                : ` in the selected ${timeRange} period`
              }
            </p>
          </div>
          <BarChart3 className="w-5 h-5 text-gray-400" />
        </div>

        {metricsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg animate-pulse">
                <div className="w-full h-4 bg-gray-200 rounded mb-2"></div>
                <div className="w-16 h-6 bg-gray-200 rounded mb-1"></div>
                <div className="w-12 h-4 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : activityMetrics?.data?.events?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p>No activity metrics available for this period</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {getOrderedMetrics(activityMetrics?.data?.events || []).map((event, index) => {
              // Course Started follows the theme colour; other cards keep their API colour
              const tint = (alpha: number, hexAlpha: string) =>
                event.event_type === 'COURSE_STARTED' ? brandColor(alpha) : `${event.color}${hexAlpha}`;
              const gradientStyle = {
                background: `linear-gradient(135deg, ${tint(0.08, '15')}, ${tint(0.02, '05')})`,
                borderColor: tint(0.19, '30')
              };
              
              return (
                <div 
                  key={index} 
                  className="p-4 rounded-xl border hover:shadow-lg transition-all duration-300"
                  style={gradientStyle}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg"
                      style={{ 
                        background: `linear-gradient(135deg, ${tint(1, '')}, ${tint(0.8, 'CC')})`,
                        boxShadow: `0 4px 15px ${tint(0.25, '40')}`
                      }}
                    >
                      {getActivityIcon(event.icon)}
                    </div>
                    <span 
                      className="text-xs font-bold px-2 py-1 rounded-full"
                      style={{ 
                        backgroundColor: tint(0.12, '20'),
                        color: tint(1, '')
                      }}
                    >
                      {event.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg mb-1">{event.count.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 leading-tight font-medium">{event.event_name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Activities Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Activities</h2>
            <p className="text-sm text-gray-500 mt-1">
              {recentActivitiesResponse?.meta_data?.total || 0} total activities
            </p>
          </div>
        </div>

        {activitiesLoading ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Activity</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Device</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">IP Address</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Time</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: pageSize }).map((_, index) => (
                  <tr key={index} className="border-b border-gray-100 animate-pulse">
                    <td className="py-4 px-4">
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-32 h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="w-48 h-3 bg-gray-200 rounded"></div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-24 h-4 bg-gray-200 rounded mb-1"></div>
                      <div className="w-32 h-3 bg-gray-200 rounded"></div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-20 h-4 bg-gray-200 rounded mb-1"></div>
                      <div className="w-16 h-3 bg-gray-200 rounded"></div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-28 h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="w-16 h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No activities found</p>
            {debouncedSearchQuery && (
              <p className="text-sm mt-2">Try adjusting your search criteria</p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Activity</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Performed By</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Device</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity._id} className="border-b border-gray-100 hover:bg-gray-50">
                      {/* Status */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(activity.status)}
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            activity.status === 'success' ? 'bg-brand-green-100 text-brand-green-700' :
                            activity.status === 'warning' ? 'bg-brand-yellow-100 text-brand-yellow-700' :
                            activity.status === 'info' ? 'bg-brand-blue-100 text-brand-blue-700' :
                            activity.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {activity.status}
                          </span>
                        </div>
                      </td>

                      {/* Activity */}
                      <td className="py-4 px-4">
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm flex-shrink-0"
                            style={{ backgroundColor: activity.status === 'error' ? '#F44336' : brandColor(1) }}
                          >
                            {getActivityIcon(activity.icon)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{activity.action}</p>
                            <p className="text-xs text-gray-600 mt-1 max-w-md">{activity.details}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              <span className="font-medium">Type:</span> {activity.type}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* User */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{activity.metadata.user_name}</p>
                            <p className="text-xs text-gray-600">{activity.metadata.user_email}</p>
                            <p className="text-xs text-gray-400">ID: {activity.metadata.user_id}</p>
                          </div>
                        </div>
                      </td>

                      {/* Performed By */}
                      <td className="py-4 px-4">
                        {activity.metadata.action_by_name && 
                         activity.metadata.action_by_name !== activity.metadata.user_name ? (
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{activity.metadata.action_by_name}</p>
                              <p className="text-xs text-gray-600">{activity.metadata.action_by_email}</p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Self</span>
                        )}
                      </td>

                      {/* Device */}
                      <td className="py-4 px-4">
                        {activity.device_info ? (
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {activity.device_info.browser} {activity.device_info.browser_version}
                            </p>
                            <p className="text-xs text-gray-600">
                              {activity.device_info.os} {activity.device_info.os_version}
                            </p>
                            <p className="text-xs text-gray-500">{activity.device_info.device}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Unknown</span>
                        )}
                      </td>

                      {/* IP Address */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-mono text-xs text-gray-900">
                            {activity.metadata.ip_address || 'Unknown'}
                          </span>
                        </div>
                      </td>

                      {/* Time */}
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-sm text-gray-900">{formatTimeAgo(activity.timestamp)}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(recentActivitiesResponse?.meta_data?.total ?? 0) > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={recentActivitiesResponse?.meta_data?.total || 0}
                itemsPerPage={pageSize}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                }}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </>
        )}
      </div>

    </div>
  );
}